// PointAdjustmentsPage.jsx
// UI restyled to Opera Cloud design system.
// ALL business logic, API calls, state, and form validation are unchanged.

import React, { useState, useEffect } from 'react';
import '../../styles/operaCloud.css';        // shared design tokens & base classes
import './PointAdjustmentsPage.css';          // page-specific layout
import { postUnityPointsAdjustment, postTierPointsAdjustment, getLocation, getCurrencies, convertCurrencyToPoints, convertPointsToCurrency } from './operaExpClient';
import useAuthSafe from '../../auth/useAuthSafe';
import { useAuthorization } from '../../auth/useAuthorization';
import UnauthorizedPage from '../../components/UnauthorizedPage';

// ── Point adjustment limits ────────────────────────────────────────────────
const UNITY_POINTS_LIMIT = 2_000_000;  // max points for a unity-only adjustment
const TIER_POINTS_LIMIT  = 3_000;      // max points when "Update Tier Credits" = Yes

export default function PointAdjustmentsPage({ isModal = false, onClose = null }) {
  const auth = useAuthSafe();
  const { canAccessPointAdjustments, isAuthLoading } = useAuthorization();
  const givenName  = auth.user?.profile?.given_name  ?? '';
  const familyName = auth.user?.profile?.family_name ?? '';
  const adjustmentUser = givenName && familyName ? `${givenName}.${familyName}` : undefined;

  // ─── State (100% unchanged) ──────────────────────────────────────────────
  const [pmsHotelId,             setPmsHotelId]             = useState('');
  const [propertyCode,           setPropertyCode]           = useState('');
  const [propertyName,           setPropertyName]           = useState('');
  const [nameLoading,            setNameLoading]            = useState(false);
  const [nameError,              setNameError]              = useState(null);
  const [isValidProperty,        setIsValidProperty]        = useState(false);
  const [memberId,               setMemberId]               = useState('');
  const [adjustmentType,         setAdjustmentType]         = useState('credit');
  const [points,                 setPoints]                 = useState('');
  const [currencyCode,           setCurrencyCode]           = useState('USD');
  const [currencyValue,          setCurrencyValue]          = useState('');
  const [reason,                 setReason]                 = useState('');
  const [comment,                setComment]                = useState('');
  const [updateTierCredits,      setUpdateTierCredits]      = useState('No');
  const [loading,                setLoading]                = useState(false);
  const [converting,             setConverting]             = useState(false);
  const [pointsConversionError,  setPointsConversionError]  = useState('');
  const [currencyConversionError,setCurrencyConversionError]= useState('');
  const [successResponse,        setSuccessResponse]        = useState(null);
  const [errorMessage,           setErrorMessage]           = useState(null);
  const [showToast,              setShowToast]              = useState(false);
  const [toastData,              setToastData]              = useState(null);
  const [currencies,             setCurrencies]             = useState([]);
  const [currenciesLoading,      setCurrenciesLoading]      = useState(false);
  const [propertyCurrency,       setPropertyCurrency]       = useState('USD');

  // Derived limit validation — recomputes whenever points or updateTierCredits changes
  const pointsLimitError = (() => {
    if (points === '' || isNaN(Number(points))) return '';
    const abs = Math.abs(Number(points));
    if (updateTierCredits === 'Yes' && abs > TIER_POINTS_LIMIT) {
      return `Maximum allowed is ${TIER_POINTS_LIMIT.toLocaleString()} points when 'tier credits' are selected.`;
    }
    if (abs > UNITY_POINTS_LIMIT) {
      return `Maximum allowed is ${UNITY_POINTS_LIMIT.toLocaleString()} Unity points.`;
    }
    return '';
  })();

  const isFormValid = memberId && currencyValue && propertyCode && reason && !pointsLimitError;

  // ─── Submit (unchanged) ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setSuccessResponse(null);
    setErrorMessage(null);

    try {
      // ── Determine unity points to send ────────────────────────────────
      // If propertyCurrency === USD → no change, use points as-is.
      //
      // If propertyCurrency !== USD AND user selected a non-USD currency:
      //   Re-convert the original currency amount using USD as the currency
      //   to get USD-equivalent points.
      //   e.g. 50 EUR → 5850pts (display), re-convert 50 USD → 5000pts (sent)
      //
      // If propertyCurrency !== USD AND user selected USD as the currency:
      //   Step 1: Convert the already-converted points back to property currency.
      //           e.g. 1000pts → convertPointsToCurrency(1000, propertyCurrency=EUR) → 8.7 EUR
      //   Step 2: Convert that property-currency amount back to points using USD.
      //           e.g. convertCurrencyToPoints(8.7, USD) → 870pts (sent)
      //   Tier always uses the original points (1000/3).
      let unityPoints = Number(points);
      if (propertyCurrency && propertyCurrency !== 'USD') {
        const numericPoints   = Number(points);
        const numericCurrency = Number(currencyValue);
        const sign            = numericCurrency < 0 ? -1 : 1;
        const today           = new Date().toISOString().split('T')[0];

        if (currencyCode === 'USD') {
          // Sub-case: user selected USD on a non-USD property
          // Step 1 — points → property currency
          const ptsToCurrResult = await convertPointsToCurrency({
            memberId,
            points: Math.abs(numericPoints),
            currencyCode: propertyCurrency,
            propertyCode,
            exchangeDate: today
          });
          if (ptsToCurrResult?.convertedValue === undefined) {
            throw new Error('Currency conversion failed: could not convert points to property currency. Please try again.');
          }
          const propCurrencyAmount = ptsToCurrResult.convertedValue;
          // Step 2 — property currency amount → USD points
          const currToUsdPtsResult = await convertCurrencyToPoints({
            memberId,
            currencyValue: propCurrencyAmount,
            currencyCode: 'USD',
            propertyCode,
            exchangeDate: today
          });
          if (currToUsdPtsResult?.convertedValue === undefined) {
            throw new Error('Currency conversion failed: could not convert property currency to USD points. Please try again.');
          }
          unityPoints = sign * currToUsdPtsResult.convertedValue;
        } else {
          // Sub-case: user selected a non-USD currency on a non-USD property
          // Re-convert the same currency amount using USD to get USD-equivalent points
          const absCurrency = Math.abs(numericCurrency);
          const usdConvResult = await convertCurrencyToPoints({
            memberId,
            currencyValue: absCurrency,
            currencyCode: 'USD',
            propertyCode,
            exchangeDate: today
          });
          if (usdConvResult?.convertedValue === undefined) {
            throw new Error('Currency conversion failed: could not convert currency amount to USD points. Please try again.');
          }
          unityPoints = sign * usdConvResult.convertedValue;
        }
      }

      const unityResp = await postUnityPointsAdjustment({
        memberId,
        points: unityPoints,
        propertyCode,
        exchangeDate: new Date().toISOString(), // always current date & time at the moment of submission
        reason,
        adjustmentUser
      });

      // Unity Point Adjustment succeeded (debug details removed)

      let tierResp = null;
      if (updateTierCredits === 'Yes') {
        try {
          // Tier points = round(originalPoints / 3) — always based on original conversion, sign preserved
          const tierPoints = Math.round(Math.abs(Number(points)) / 3) * (Number(points) < 0 ? -1 : 1);
          tierResp = await postTierPointsAdjustment({
            memberId,
            points: tierPoints,
            propertyCode,
            adjustmentDateTime: new Date().toISOString(), // always current date & time at the moment of submission
            reason,
            adjustmentUser
          });
          // Tier Point Adjustment succeeded (debug details removed)
        } catch (tierErr) {
          console.warn('Tier adjustment failed');
        }
      }

      setSuccessResponse({ unity: unityResp, tier: tierResp });

      try {
        const unityBal = (unityResp && (unityResp.updatedBalance ?? unityResp.balance ?? unityResp.updatedBalanceAmount))?.toString();
        const tierBal  = (tierResp  && (tierResp.updatedBalance  ?? tierResp.balance  ?? tierResp.updatedAmount))?.toString();
        setToastData({ unity: unityBal || null, tier: tierBal || null });
        setShowToast(true);
      } catch (e) { /* ignore toast errors */ }

      // Reset form on success
      setPoints('');
      setCurrencyValue('');
      setReason('');
      setComment('');
    } catch (err) {
      console.error('Point Adjustment Error');

      let msgObj  = null;
      let msgText = 'Failed to process point adjustment';
      if (err?.response?.error || err?.response?.message) {
        msgObj  = err.response.error || { message: err.response.message };
        msgText = err.response.error?.message || err.response.message || msgText;
      } else if (err?.message) {
        msgObj  = { message: err.message };
        msgText = err.message;
      } else {
        msgObj = { message: msgText };
      }
      setErrorMessage(msgObj);

      try {
        setToastData({ isError: true });
        setShowToast(true);
      } catch (e) { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };

  // ─── Reset (resets editable fields only; property & memberId come from URL) ─
  const handleReset = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      setPoints('');
      setCurrencyCode(propertyCurrency || 'USD');
      setCurrencyValue('');
      setReason('');
      setComment('');
      setUpdateTierCredits('No');
      setPointsConversionError('');
      setCurrencyConversionError('');
      setSuccessResponse(null);
      setErrorMessage(null);
    }
  };

  // ─── Points → Currency ────────────────────────────────────────────────────
  const handlePointsBlur = async () => {
    // Reset both conversion errors so the user sees a clean slate
    setPointsConversionError('');
    setCurrencyConversionError('');
    if (points && !converting && propertyCode) {
      setConverting(true);
      try {
        const { convertPointsToCurrency } = await import('./operaExpClient');
        // Use absolute value for conversion; actual (signed) value is used in POST
        const numericPoints = Number(points);
        const absPoints = Math.abs(numericPoints);
        const sign = numericPoints < 0 ? -1 : 1;
        const result = await convertPointsToCurrency({ memberId, points: absPoints, currencyCode, propertyCode, exchangeDate: new Date().toISOString().split('T')[0] });
        if (result?.convertedValue !== undefined) {
          // Apply original sign so the displayed currency value reflects the negative input
          setCurrencyValue((sign * result.convertedValue).toString());
        }
      } catch (err) {
        console.error('Points to currency conversion error');
        const detail = err?.response?.detail || '';
        setPointsConversionError(detail ? `error converting points to currency - ${detail}` : 'error converting points to currency');
        setPoints('');
        setCurrencyValue('');
      } finally {
        setConverting(false);
      }
    }
  };

  // ─── Currency → Points ────────────────────────────────────────────────────
  const handleCurrencyValueBlur = async () => {
    // Reset both conversion errors so the user sees a clean slate
    setCurrencyConversionError('');
    setPointsConversionError('');
    if (currencyValue && !converting && propertyCode) {
      setConverting(true);
      try {
        const { convertCurrencyToPoints } = await import('./operaExpClient');
        // Use absolute value for conversion; preserve the sign for the POST adjustment
        const numericCurrency = Number(currencyValue);
        const absCurrency = Math.abs(numericCurrency);
        const sign = numericCurrency < 0 ? -1 : 1;
        const result = await convertCurrencyToPoints({ memberId, currencyValue: absCurrency, currencyCode, propertyCode, exchangeDate: new Date().toISOString().split('T')[0] });
        if (result?.convertedValue !== undefined) {
          // Apply original sign so the POST adjustment carries the correct sign
          setPoints((sign * result.convertedValue).toString());
        }
      } catch (err) {
        console.error('Currency to points conversion error');
        const detail = err?.response?.detail || '';
        setCurrencyConversionError(detail ? `error converting currency to points - ${detail}` : 'error converting currency to points');
        setCurrencyValue('');
        setPoints('');
      } finally {
        setConverting(false);
      }
    }
  };

  // ─── URL param read ────────────────────────────────────────────────────────
  useEffect(() => {
    const urlParams            = new URLSearchParams(window.location.search);
    const receivedMemberId     = urlParams.get('memberId') || urlParams.get('accountNumber');
    const receivedPmsHotelId   = urlParams.get('pmsHotelId');
    if (receivedMemberId)   { /* memberId from URL (log removed for privacy) */ setMemberId(receivedMemberId); }
    if (receivedPmsHotelId) { /* pmsHotelId from URL (log removed for privacy) */ setPmsHotelId(receivedPmsHotelId); }
  }, []);

  // ─── Property name + currencies fetch (parallel) ────────────────────────
  // Calls location/{pmsHotelId} and /currencies in parallel.
  // locationLegalName  → displayed on UI as the property name
  // locationCode       → stored as propertyCode and sent in all subsequent API calls
  useEffect(() => {
    // Guard — no API call if user is not authorized.
    if (!canAccessPointAdjustments) return;
    let cancelled = false;
    async function fetchPageData(id) {
      if (!id) return;
      setNameLoading(true);
      setCurrenciesLoading(true);
      setNameError(null);
      try {
        const [locationRes, currenciesRes] = await Promise.all([
          getLocation(id),
          getCurrencies()
        ]);

        if (cancelled) return;

        // Handle location
        if (locationRes?.locationLegalName && locationRes?.locationCode) {
          setPropertyName(locationRes.locationLegalName);
          setPropertyCode(locationRes.locationCode);
          setPropertyCurrency(locationRes.currency || 'USD');
          setIsValidProperty(true);
        } else {
          setPropertyName('');
          setPropertyCode('');
          setPropertyCurrency('USD');
          setIsValidProperty(false);
        }

        // Handle currencies — unique, non-null currencyCodes, sorted alphabetically
        if (Array.isArray(currenciesRes)) {
          const seen = new Set();
          const uniqueCurrencies = currenciesRes
            .filter(c => c.currencyCode != null)
            .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode))
            .filter(c => {
              if (seen.has(c.currencyCode)) return false;
              seen.add(c.currencyCode);
              return true;
            });
          const defaultCurrency = locationRes?.currency || 'USD';
          const currenciesWithDefault = uniqueCurrencies.some(c => c.currencyCode === defaultCurrency)
            ? uniqueCurrencies
            : [{ currencyCode: defaultCurrency }, ...uniqueCurrencies];
          setCurrencies(currenciesWithDefault);
          if (currenciesWithDefault.length > 0) {
            setCurrencyCode(defaultCurrency);
          }
        }
      } catch (err) {
        console.error('Failed to fetch page data for pmsHotelId');
        if (!cancelled) { setNameError(err); setPropertyName(''); setPropertyCode(''); setIsValidProperty(false); }
      } finally {
        if (!cancelled) { setNameLoading(false); setCurrenciesLoading(false); }
      }
    }
    if (pmsHotelId) fetchPageData(pmsHotelId);
    return () => { cancelled = true; };
  }, [pmsHotelId, canAccessPointAdjustments]);

  // ─── Toast auto-dismiss (unchanged) ──────────────────────────────────────
  useEffect(() => {
    if (!showToast) return;
    const t = setTimeout(() => setShowToast(false), 40000);
    return () => clearTimeout(t);
  }, [showToast]);

  // ─────────────────────────────────────────────────────────────────────────
  // ACCESS GUARD — placed after all hooks (Rules of Hooks compliance).
  // If the user is not in an allowed Point Adjustment group, show the
  // UnauthorizedPage. Auto-re-evaluates after token silent-renewal.
  // ─────────────────────────────────────────────────────────────────────────
  if (isAuthLoading) {
    // Auth is still being established (loading, renewing, or re-logging in).
    // Return null — ProtectedApp is showing the appropriate loading/login UI.
    // Never show UnauthorizedPage during a transitional auth state.
    return null;
  }

  if (!canAccessPointAdjustments) {
    console.warn(
      '[PointAdjustments] Access denied — user is not a member of any allowed group. ' +
      'Contact your administrator to be added to the appropriate group '
    );
    return <UnauthorizedPage pageName="Point Adjustments" />;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Loading / empty guard (unchanged logic, Opera Cloud UI)
  // ─────────────────────────────────────────────────────────────────────────
  const hasMemberAndValidProperty = !!memberId && !!pmsHotelId && isValidProperty;

  if (!hasMemberAndValidProperty) {
    return (
      <div className="paFullscreen">
        <div className="paFullscreenBody">
          <div className="paCard">
            <div className="paEmptyState">
              {nameLoading ? 'Loading...' : 'No data available'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FORM CONTENT
  // ─────────────────────────────────────────────────────────────────────────
  const formContent = (
    <div className="paCard">

      <div className="paCardBody">
        <form onSubmit={handleSubmit} className="paForm">

          {/* Property — display only */}
          <div className="paField">
            <label className="oc-label">Property</label>
            <div className="paDisplayValue">
              {nameLoading ? 'Loading property...' : (propertyName || propertyCode)}
            </div>
          </div>

          {/* Member ID — display only */}
          <div className="paField">
            <label className="oc-label">Member ID</label>
            <div className="paDisplayValue">{memberId}</div>
          </div>

          {/* Unity Points Adjustment */}
          <div className="paField">
            <div className="paFieldLabelRow">
              <label className="oc-label">Unity Points Adjustment</label>
              {pointsConversionError && (
                <span className="paFieldError" role="alert">{pointsConversionError}</span>
              )}
            </div>
            <div className="paInlineRow">
              {/* Fixed "Points" label cell */}
              <div className="paDisplayValue">Points</div>
              <input
                type="number"
                className={`oc-input${pointsLimitError ? ' pa-input-error' : ''}`}
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                onBlur={handlePointsBlur}
                step="any"
                placeholder="Enter points"
                required
                disabled={loading || converting}
              />
            </div>
            {pointsLimitError && (
              <div className="paLimitError" role="alert">{pointsLimitError}</div>
            )}
          </div>

          {/* Currency */}
          <div className="paField">
            <div className="paFieldLabelRow">
              <label className="oc-label">Currency</label>
              {currencyConversionError && (
                <span className="paFieldError" role="alert">{currencyConversionError}</span>
              )}
            </div>
            <div className="paInlineRow">
              <select
                className="oc-select"
                value={currencyCode}
                onChange={(e) => {
                  setCurrencyCode(e.target.value);
                  setPoints('');
                  setCurrencyValue('');
                  setPointsConversionError('');
                  setCurrencyConversionError('');
                }}
                disabled={loading || currenciesLoading}
              >
                {currenciesLoading ? (
                  <option value="">Loading...</option>
                ) : (
                  currencies.map(c => (
                    <option key={c.currencyCode} value={c.currencyCode}>{c.currencyCode}</option>
                  ))
                )}
              </select>
              <input
                type="number"
                className="oc-input"
                value={currencyValue}
                onChange={(e) => setCurrencyValue(e.target.value)}
                onBlur={handleCurrencyValueBlur}
                step="0.01"
                placeholder="Enter currency value"
                disabled={loading || converting}
              />
            </div>
          </div>

          {/* Reason for Adjustment */}
          <div className="paField">
            <label className="oc-label">Reason for Adjustment</label>
            <select
              className="oc-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              disabled={loading}
            >
              <option value="">Select a reason</option>
              <option value="1000 Choice Points">1000 Choice Points</option>
              <option value="500 Choice Points">500 Choice Points</option>
              <option value="Choice Adjustment (-)">Choice Adjustment (-)</option>
              <option value="Meeting Planner Adjustment (-)">Meeting Planner Adjustment (-)</option>
              <option value="Meeting Planner Incentive">Meeting Planner Incentive</option>
              <option value="Redemption Adjustment">Redemption Adjustment</option>
              <option value="Service Recovery">Service Recovery</option>
              <option value="Service Recovery Adjustment (-)">Service Recovery Adjustment (-)</option>
            </select>
          </div>

          {/* Update Tier Credits */}
          <div className="paField">
            <label className="oc-label">Update Tier Credits</label>
            <select
              className="oc-select"
              style={{ width: 110 }}
              value={updateTierCredits}
              onChange={(e) => setUpdateTierCredits(e.target.value)}
              disabled={loading}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>

          {/* Comment (Optional) — kept commented out per original */}
          {/*
          <div className="paField">
            <label className="oc-label">Comment (Optional)</label>
            <textarea
              className="oc-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter additional comments"
              rows="2"
              disabled={loading}
            />
          </div>
          */}

          {/* Action buttons */}
          <div className="paButtonRow">
            <button
              type="button"
              className="oc-btn oc-btn-secondary"
              onClick={handleReset}
              disabled={loading}
            >
              {isModal ? 'Cancel' : 'Reset'}
            </button>
            <button
              type="submit"
              className="oc-btn"
              disabled={!isFormValid || loading}
            >
              {loading ? 'Processing...' : 'Post Adjustment'}
            </button>
          </div>

        </form>

        {/* Backend error */}
        {errorMessage && (
          <div className="paBackendError">
            <div className="paBackendErrorIcon">✕</div>
            <div className="paBackendErrorContent">
              <div className="paBackendErrorTitle">
                {errorMessage.code && errorMessage.message
                  ? `Error: ${errorMessage.code} is not valid`
                  : 'Error occurred'}
              </div>
              <div className="paBackendErrorMessage">
                {errorMessage.message || 'Please enter a valid value'}
              </div>
            </div>
          </div>
        )}

      </div>
      {/* end .paCardBody */}

    </div>
  );

  // ─── Modal mode — return bare form ───────────────────────────────────────
  if (isModal) return formContent;

  // ─── Full-page (standalone iframe) ───────────────────────────────────────
  return (
    <div className="paFullscreen">
      <div className="paFullscreenBody">
        {formContent}
      </div>

      {/* Toast — outside card so it overlays everything */}
      {showToast && toastData && (
        <div className={`paToast${toastData.isError ? ' paToastError' : ''}`} role="status" aria-live="polite">
          <div className="paToastHeader">
            <span>{toastData.isError ? 'Post adjustment failed' : 'Point Adjustment Successful'}</span>
            <button className="paToastCloseBtn" onClick={() => setShowToast(false)} aria-label="dismiss">×</button>
          </div>
          {!toastData.isError && (
            <div className="paToastBody">
              {toastData.unity && <div>Unity points balance: <strong>{toastData.unity}</strong></div>}
              {toastData.tier  && <div>Tier points balance: <strong>{toastData.tier}</strong></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}